import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Camera, Search, Loader2, AlertCircle, Check, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Mobile-friendly barcode/QR scanner.
 *  - Uses native `BarcodeDetector` API where available (Chrome on Android,
 *    Edge, Safari ≥17 on iOS). Otherwise falls back to manual entry.
 *  - On a detected code, immediately calls /assets/lookup/{code} and
 *    navigates to the asset detail page.
 */
export default function AssetScanPage() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const detectorRef = useRef(null);
  const streamRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState('');
  const [enrichPrompt, setEnrichPrompt] = useState(null); // { code }
  const [enrichBusy, setEnrichBusy] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !('BarcodeDetector' in window)) {
      setUnsupported(true);
    } else {
      try {
        // eslint-disable-next-line no-undef
        detectorRef.current = new BarcodeDetector({ formats: ['qr_code', 'code_128', 'ean_13', 'code_39'] });
      } catch (e) {
        setUnsupported(true);
      }
    }
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCamera = async () => {
    setLastError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setScanning(true);
      scanLoop();
    } catch (err) {
      setLastError(err.message || 'Camera access denied');
    }
  };

  const stopCamera = () => {
    setScanning(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const scanLoop = async () => {
    if (!scanning && !streamRef.current) return;
    if (!detectorRef.current || !videoRef.current) return;
    try {
      const codes = await detectorRef.current.detect(videoRef.current);
      if (codes.length > 0) {
        const raw = codes[0].rawValue;
        stopCamera();
        await handleCode(raw);
        return;
      }
    } catch (e) { /* ignore frame errors */ }
    if (streamRef.current) requestAnimationFrame(scanLoop);
  };

  const handleCode = async (code) => {
    setBusy(true);
    setLastError('');
    try {
      const r = await axios.get(`${API_URL}/api/assets/lookup/${encodeURIComponent(code)}`, { headers: authHeader() });
      toast.success(`Found ${r.data.asset_tag}`);
      navigate(`/assets/${r.data.id}`);
    } catch (err) {
      if (err.response?.status === 404) {
        // Not in inventory — offer to web-enrich + create
        setEnrichPrompt({ code });
      } else {
        setLastError(err.response?.data?.detail || `Lookup failed for "${code}"`);
      }
    } finally { setBusy(false); }
  };

  const handleEnrichCreate = async () => {
    if (!enrichPrompt) return;
    setEnrichBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/assets/from-barcode`,
        { code: enrichPrompt.code }, { headers: authHeader() });
      const a = r.data.asset;
      const p = r.data.product || {};
      if (p.source === 'unknown') {
        toast.message('Created — web lookup could not identify product', {
          description: 'You can edit the details on the asset page.',
        });
      } else {
        toast.success(`Created ${a.asset_tag} (${p.manufacturer || ''} ${p.model || ''})`.trim());
      }
      navigate(`/assets/${a.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally { setEnrichBusy(false); setEnrichPrompt(null); }
  };

  const handleEnrichPrefillForm = () => {
    if (!enrichPrompt) return;
    navigate(`/assets/new?upc=${encodeURIComponent(enrichPrompt.code)}`);
  };

  const submitManual = (e) => {
    e?.preventDefault();
    if (!manualCode.trim()) return;
    handleCode(manualCode.trim());
  };

  return (
    <div className="p-6 max-w-2xl mx-auto" data-testid="asset-scan-page">
      <Link to="/assets" className="text-sm text-[#0E7490] hover:underline inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Workspace
      </Link>
      <h1 className="text-2xl font-bold text-[#0A2342] mt-2 mb-4">Scan asset code</h1>

      <Card className="p-5 border-[#E2E8F0] space-y-4">
        {unsupported && (
          <div className="px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Camera scanning requires Chrome on Android, Edge, or Safari 17+. On unsupported browsers, use manual entry below.</span>
          </div>
        )}

        {!scanning ? (
          <Button onClick={startCamera} disabled={unsupported} className="w-full bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="start-camera-btn">
            <Camera className="w-4 h-4 mr-2" /> Start camera scan
          </Button>
        ) : (
          <div className="relative">
            <video ref={videoRef} className="w-full rounded-lg bg-black aspect-[4/3]" playsInline muted />
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-x-12 top-1/2 -translate-y-1/2 h-1/2 border-2 border-[#0E7490] rounded-lg" />
            </div>
            <Button onClick={stopCamera} variant="outline" size="sm" className="absolute top-2 right-2" data-testid="stop-camera-btn">
              <X className="w-4 h-4" /> Stop
            </Button>
            <p className="text-xs text-[#64748B] mt-2 text-center">Point camera at QR / Code-128 label</p>
          </div>
        )}

        <form onSubmit={submitManual} className="border-t border-[#E2E8F0] pt-4">
          <div className="text-xs text-[#64748B] mb-2">Or enter the asset tag manually:</div>
          <div className="flex gap-2">
            <Input placeholder="SVN-XXX-00001" value={manualCode} onChange={e => setManualCode(e.target.value)}
                   className="font-mono uppercase" data-testid="manual-code-input" />
            <Button type="submit" disabled={!manualCode.trim() || busy} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="manual-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
        </form>

        {lastError && (
          <div className="px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-xs text-rose-700 flex items-center gap-2" data-testid="scan-error">
            <X className="w-4 h-4" /> {lastError}
          </div>
        )}
      </Card>

      {enrichPrompt && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="enrich-modal">
          <Card className="max-w-md w-full p-6 space-y-4 border-[#E2E8F0]">
            <div>
              <h3 className="text-lg font-semibold text-[#0A2342]">Item not in inventory</h3>
              <p className="text-xs text-[#64748B] mt-1">
                <span className="font-mono text-[#0A2342]">{enrichPrompt.code}</span> wasn't found. Would you like to look it up
                online and add it now?
              </p>
            </div>
            <div className="rounded-md bg-[#F0F9FF] border border-[#BAE6FD] p-3 text-[11px] text-[#075985]">
              We'll query our AI lookup for the product name, manufacturer, model,
              SKU, and warranty info, then create a new asset for your organization.
            </div>
            <div className="flex flex-col gap-2">
              <Button onClick={handleEnrichCreate} disabled={enrichBusy}
                      className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="enrich-create-btn">
                {enrichBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Look up &amp; create asset
              </Button>
              <Button onClick={handleEnrichPrefillForm} disabled={enrichBusy} variant="outline" data-testid="enrich-prefill-btn">
                Open new asset form (manual)
              </Button>
              <button onClick={() => setEnrichPrompt(null)} disabled={enrichBusy}
                      className="text-xs text-[#64748B] hover:text-[#0A2342] mt-1" data-testid="enrich-cancel-btn">
                Cancel
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
